import { computed, unref } from 'vue';
import { storeToRefs } from 'pinia';
import type { MessageListItem } from '@/common/bot/types';
import { useWidgetConversation } from '@/stores/useWidgetConversation';
import { useVoiceTranscript } from '@/stores/useVoiceTranscript';
import { InteractionView, useChatWidget } from '@/stores/useChatWidget';

/**
 * Stable key for the single Voice AI transcript row in the feed. There is at
 * most one transcript block per conversation, so a constant key is enough.
 */
export const VOICE_TRANSCRIPT_FEED_ID = 'voice-transcript';

export type FeedItem =
  | {
      kind: 'message';
      key: string;
      message: MessageListItem;
      previous?: MessageListItem;
      next?: MessageListItem;
    }
  | { kind: 'voice-transcript'; key: string };

/**
 * Composes the chat feed shown in the overlay.
 *
 * Owns the message-merging/sorting and the placement
 * so the rendering component stays purely presentational.
 */
export const useChatFeed = () => {
  const widgetConversation = useWidgetConversation();
  const { conversation, supportWizard } = storeToRefs(widgetConversation);

  const voiceTranscriptStore = useVoiceTranscript();
  const { messages: voiceTranscriptMessages, endedAt: transcriptEndedAt } = storeToRefs(voiceTranscriptStore);

  const chatWidget = useChatWidget();
  const { interactionView } = storeToRefs(chatWidget);

  const sortedMessages = computed<MessageListItem[]>(() => {
    const allMessages = [
      ...unref(conversation.value.messages),
      ...unref(supportWizard.value.support.chat.chatMessages),
    ];

    return allMessages.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
  });

  const showVoiceTranscript = computed(() => {
    return (
      interactionView.value === InteractionView.LANDING_AI_VOICE_INDICATOR || voiceTranscriptMessages.value.length > 0
    );
  });

  /**
   * The transcript renders as a fixed block in the feed anchored at the call's
   * end time (`transcriptEndedAt`). Messages created before the call ended
   * render above it; everything created afterwards (post-handoff channel
   * picker, live chat, wizard steps) renders below it, preserving chronological
   * order. While the call is still ongoing (`endedAt === null`) the block is
   * anchored to the bottom of the feed.
   */
  const feedItems = computed<FeedItem[]>(() => {
    type Entry = { sortKey: number; message?: MessageListItem };

    const entries: Entry[] = sortedMessages.value.map((message) => ({
      sortKey: message.timestamp || 0,
      message,
    }));

    if (showVoiceTranscript.value) {
      entries.push({ sortKey: transcriptEndedAt.value ?? Number.POSITIVE_INFINITY });
    }

    entries.sort((a, b) => {
      if (a.sortKey !== b.sortKey) return a.sortKey - b.sortKey;
      // On a timestamp tie the transcript anchors before equal-timed messages,
      // matching the original `< endedAt` (above) / `>= endedAt` (below) split.
      if (!a.message && b.message) return -1;
      if (a.message && !b.message) return 1;
      return 0;
    });

    return entries.map((entry, idx) => {
      if (!entry.message) {
        return { kind: 'voice-transcript', key: VOICE_TRANSCRIPT_FEED_ID };
      }

      return {
        kind: 'message',
        key: `${entry.message.id}`,
        message: entry.message,
        previous: entries[idx - 1]?.message,
        next: entries[idx + 1]?.message,
      };
    });
  });

  return {
    feedItems,
    voiceTranscriptMessages,
  };
};
